import { createContext, useContext, useCallback, useState, useMemo, ReactNode } from 'react';
import invariant from 'tiny-invariant';

interface QuestionExpandContextValue {
    /** Set of expanded positions - consumers check membership directly */
    expandedPositions: ReadonlySet<number>;
    /** Toggle a single question's expand state */
    toggleExpand: (position: number) => void;
    /** Expand a specific question */
    expand: (position: number) => void;
    /** Expand all questions */
    expandAll: (questionCount: number) => void;
    /** Collapse all questions */
    collapseAll: () => void;
    /** Adjust positions after drag-and-drop reorder */
    adjustPositionsForReorder: (oldIndex: number, newIndex: number) => void;
    /** Adjust positions after a question is deleted */
    handleQuestionDeleted: (deletedPosition: number) => void;
    /** Adjust positions after a question is restored (undo delete) */
    handleQuestionRestored: (restoredPosition: number, wasExpanded: boolean) => void;
}

const QuestionExpandContext = createContext<QuestionExpandContextValue | null>(null);

export const QuestionExpandProvider = ({ children }: { children: ReactNode }) => {
    const [expandedPositions, setExpandedPositions] = useState<Set<number>>(new Set());

    // All callbacks use functional setState - no dependencies needed, stable references
    const toggleExpand = useCallback((position: number) => {
        setExpandedPositions((prev) => {
            const next = new Set(prev);
            if (next.has(position)) {
                next.delete(position);
            } else {
                next.add(position);
            }
            return next;
        });
    }, []);

    const expand = useCallback((position: number) => {
        setExpandedPositions((prev) => {
            if (prev.has(position)) return prev;
            const next = new Set(prev);
            next.add(position);
            return next;
        });
    }, []);

    const expandAll = useCallback((questionCount: number) => {
        setExpandedPositions(new Set(Array.from({ length: questionCount }, (_, i) => i)));
    }, []);

    const collapseAll = useCallback(() => {
        setExpandedPositions(new Set());
    }, []);

    const adjustPositionsForReorder = useCallback((oldIndex: number, newIndex: number) => {
        setExpandedPositions((prev) => {
            const next = new Set<number>();
            prev.forEach((pos) => {
                if (pos === oldIndex) {
                    next.add(newIndex);
                } else if (oldIndex < newIndex) {
                    if (pos > oldIndex && pos <= newIndex) {
                        next.add(pos - 1);
                    } else {
                        next.add(pos);
                    }
                } else {
                    if (pos >= newIndex && pos < oldIndex) {
                        next.add(pos + 1);
                    } else {
                        next.add(pos);
                    }
                }
            });
            return next;
        });
    }, []);

    const handleQuestionDeleted = useCallback((deletedPosition: number) => {
        setExpandedPositions((prev) => {
            const next = new Set<number>();
            prev.forEach((pos) => {
                if (pos < deletedPosition) {
                    next.add(pos);
                } else if (pos > deletedPosition) {
                    next.add(pos - 1);
                }
            });
            return next;
        });
    }, []);

    const handleQuestionRestored = useCallback((restoredPosition: number, wasExpanded: boolean) => {
        setExpandedPositions((prev) => {
            const next = new Set<number>();
            prev.forEach((pos) => {
                if (pos < restoredPosition) {
                    next.add(pos);
                } else {
                    // Shift positions >= restoredPosition up by 1
                    next.add(pos + 1);
                }
            });
            // Restore expansion state if it was expanded before deletion
            if (wasExpanded) {
                next.add(restoredPosition);
            }
            return next;
        });
    }, []);

    // Only expandedPositions changes; all callbacks are stable
    const value = useMemo(
        () => ({
            expandedPositions,
            toggleExpand,
            expand,
            expandAll,
            collapseAll,
            adjustPositionsForReorder,
            handleQuestionDeleted,
            handleQuestionRestored,
        }),
        [
            expandedPositions,
            toggleExpand,
            expand,
            expandAll,
            collapseAll,
            adjustPositionsForReorder,
            handleQuestionDeleted,
            handleQuestionRestored,
        ],
    );

    return <QuestionExpandContext value={value}>{children}</QuestionExpandContext>;
};

/** Hook for accessing question expand context */
export const useQuestionExpand = () => {
    const ctx = useContext(QuestionExpandContext);
    invariant(ctx, 'useQuestionExpand must be used within QuestionExpandProvider');
    return ctx;
};

/** Convenience hook for a single question - returns isExpanded boolean and toggle callback */
export const useQuestionExpandState = (position: number) => {
    const { expandedPositions, toggleExpand } = useQuestionExpand();

    const isExpanded = expandedPositions.has(position);
    const toggle = useCallback(() => toggleExpand(position), [toggleExpand, position]);

    return useMemo(() => ({ isExpanded, toggle }), [isExpanded, toggle]);
};
